import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import axios from 'axios';

// Define interfaces for the API response
export interface ENO1File {
  fileId: number;
  fileName: string;
  operatorByFileUploader: string | null;
  operatorByInboundPpo: string | null;
  operatorByOutboundPpo: string | null;
  accountingYear: number;
  accountingQuarter: number;
  fileVersion: number | null;
  creationDate: string | null;
  inboundRemovalDate: string | null;
  outboundRemovalDate: string | null;
  fileStatus: string | null;
  processDate: string | null;
  inboundAcceptanceDate: string | null;
  outboundAcceptanceDate: string | null;
  notifiedDate: string | null;
  downloadDate: string | null;
  pendingOn: string | null;
  deletedAt: string | null;
  comment: boolean;
  samples: number | null;
  removalDate: string | null;
  removalDo: string | null;
  sampleCount?: number | null; // Keep for backward compatibility if used elsewhere
  comments?: string | null; // Keep for backward compatibility if used elsewhere
  validStatus?: string | null; // Keep for backward compatibility if used elsewhere
}

export interface ENO1DataResponse {
  content: ENO1File[];
  pageable: {
    pageNumber: number;
    pageSize: number;
    sort: {
      empty: boolean;
      sorted: boolean;
      unsorted: boolean;
    };
    offset: number;
    paged: boolean;
    unpaged: boolean;
  };
  last: boolean;
  totalElements: number;
  totalPages: number;
  first: boolean;
  size: number;
  number: number;
  sort: {
    empty: boolean;
    sorted: boolean;
    unsorted: boolean;
  };
  numberOfElements: number;
  empty: boolean;
}

export type TableType = 'pending' | 'inbound' | 'outbound';
export type StateType = 'all' | 'pending' | 'completed';

export interface SortConfig {
  column: string;
  direction: 'ASC' | 'DESC';
}

export interface FilterConfig {
  column: string;
  term: string;
}

interface UseENO1DataParams {
  page: number;
  size: number;
  sorts?: SortConfig[];
  filters?: FilterConfig[];
  sort?: string; // Deprecated: use sorts instead
  enabled?: boolean;
  pendingOn?: string;
  accountingYear?: string;
  table?: TableType;
  designatedOperator?: string;
  state?: StateType;
  searchColumn?: string; // Deprecated: use filters instead
  searchTerm?: string; // Deprecated: use filters instead
}

/**
 * Hook to fetch eNO1 dataset with pagination
 * Supports both legacy and new API structure
 * 
 * @param params Pagination and sorting parameters, and enabled flag for conditional fetching
 * @returns Query result with eNO1 data
 */
export const useENO1Data = ({ 
  page, 
  size, 
  sorts,
  filters,
  sort, // Legacy support
  pendingOn, 
  accountingYear, 
  table, 
  designatedOperator, 
  state,
  searchColumn, // Legacy support
  searchTerm, // Legacy support
  enabled = true 
}: UseENO1DataParams) => {
  // Combine filters array with legacy searchColumn/searchTerm for backward compatibility
  const allFilters = useMemo(() => {
    const filterList: FilterConfig[] = filters ? [...filters] : [];
    // Add legacy searchColumn/searchTerm if provided and not already in filters
    if (searchColumn && searchTerm && !filterList.some(f => f.column === searchColumn)) {
      filterList.push({ column: searchColumn, term: searchTerm });
    }
    return filterList;
  }, [filters, searchColumn, searchTerm]);

  // Combine sorts array with legacy sort for backward compatibility
  const allSorts = useMemo(() => {
    const sortList: SortConfig[] = sorts ? [...sorts] : [];
    // Add legacy sort if provided (format: "column,direction")
    if (sort && !sortList.some(s => {
      const [col] = sort.split(',');
      return s.column === col;
    })) {
      const [column, direction] = sort.split(',');
      if (column && (direction === 'ASC' || direction === 'DESC')) {
        sortList.push({ column, direction: direction as 'ASC' | 'DESC' });
      }
    }
    return sortList;
  }, [sorts, sort]);

  return useQuery<ENO1DataResponse>({
    queryKey: ['eno1Data', page, size, allSorts, allFilters, pendingOn, accountingYear, table, designatedOperator, state],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('page', String(page));
      params.append('size', String(size));
      
      // Add multiple sorting parameters if provided
      if (allSorts && allSorts.length > 0) {
        allSorts.forEach((sort) => {
          // Format for backend sorting: "fieldName,direction"
          params.append('sort', `${sort.column},${sort.direction}`);
        });
      }
      
      // Add multiple filter parameters if provided
      if (allFilters && allFilters.length > 0) {
        allFilters.forEach((filter) => {
          if (filter.column && filter.term) {
            params.append(filter.column, filter.term);
          }
        });
      }
      
      if (table) {
        params.append('table', table);
        
        if (state && state !== 'all') {
          params.append('state', state);
        }
        
        if (designatedOperator && designatedOperator !== 'ALL') {
          params.append('designatedOperator', designatedOperator);
        }
        
        if (accountingYear && accountingYear !== 'all') {
          params.append('accountingYear', accountingYear);
        }
      } else {
        if (pendingOn) {
          params.append('pendingOn', pendingOn);
        }
        
        if (accountingYear && accountingYear !== 'all') {
          params.append('accountingYear', accountingYear);
        }
      }

      const response = await axios.get(`/data-set/eno1?${params.toString()}`);
      return response.data;
    },
    staleTime: 0, // Set to 0 to ensure fresh data on pagination changes
    gcTime: 5 * 60 * 1000, // Cache for 5 minutes, but will always refetch
    enabled,
    refetchOnMount: true,
    refetchOnWindowFocus: false
  });
};